import AgoraRTC, { ICameraVideoTrack } from "../lib/agora";
import VirtualBackgroundExtension, {
  IVirtualBackgroundProcessor,
} from "agora-extension-virtual-background";
import { useEffect, useRef, useState } from "react";

export function useVirtualBackground(
  localCameraTrack: ICameraVideoTrack | null,
  blurEffectEnabledByDefault: boolean,
) {
  const [isBlurEnabled, setIsBlurEnabled] = useState<boolean>(
    blurEffectEnabledByDefault,
  );

  const vbExtensionRef = useRef<VirtualBackgroundExtension>(null);
  const vbProcessorRef = useRef<IVirtualBackgroundProcessor | null>(null);

  // Register extension once
  useEffect(() => {
    if (vbExtensionRef.current) return;
    const ext = new VirtualBackgroundExtension();
    vbExtensionRef.current = ext;
    try {
      AgoraRTC.registerExtensions([ext]);
    } catch (e) {
      console.debug("VB extension registration issue:", e);
    }
  }, []);

  // (Re)initialize processor whenever camera track changes
  useEffect(() => {
    let disposed = false;

    const setupVB = async () => {
      const ext = vbExtensionRef.current;
      if (!ext || !localCameraTrack) return;

      if (!ext.checkCompatibility()) {
        console.error(
          "Virtual background not supported on this device/browser",
        );
        return;
      }

      try {
        // Clean any previous pipeline
        if (vbProcessorRef.current) {
          try {
            vbProcessorRef.current.unpipe?.();
            await vbProcessorRef.current.disable?.();
          } catch {}
        }

        const processor = ext.createProcessor();
        vbProcessorRef.current = processor;
        await processor.init();

        // pipe: camera -> processor -> destination
        localCameraTrack
          .pipe(processor)
          .pipe(localCameraTrack.processorDestination);

        processor.setOptions({ type: "blur", blurDegree: 1 });
        if (blurEffectEnabledByDefault) {
          await processor.enable();
        }
        if (disposed) {
          await processor.disable();
        }
      } catch (e) {
        console.error("Error initializing virtual background:", e);
      }
    };

    void setupVB();

    return () => {
      disposed = true;
      const teardown = async () => {
        try {
          vbProcessorRef.current?.unpipe?.();
          localCameraTrack?.unpipe?.();
          await vbProcessorRef.current?.disable?.();
        } catch {}
      };
      void teardown();
    };
  }, [localCameraTrack, blurEffectEnabledByDefault]);

  const toggleBlur = async () => {
    const p = vbProcessorRef.current;
    if (!p) return;
    try {
      if (isBlurEnabled) {
        await p.disable();
      } else {
        await p.enable();
      }
      setIsBlurEnabled((v) => !v);
    } catch (e) {
      console.error("toggleBlur failed:", e);
    }
  };

  return {
    isBlurEnabled,
    toggleBlur,
  };
}
